import { Checkbox, Switch, Typography } from '@material-ui/core'
import { BugReportDialog } from 'components/domain/feedback/BugReportDialog'
import { ActionButton, Button } from 'components/facets'
import { PowerToolsPaper } from 'components/facets/PowerToolsPaper'
import { useUser } from 'components/providers/UserProvider'
import { deleteUserSubEntity, editUserSubEntity } from 'lib/api'
import { useConfirm } from 'lib/hooks'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PricingPlan } from 'shared'
import { SetState } from 'types'
import { useHandleSettingsChange } from '../../StockPricingPresenter'
import { EditableElement } from '../EditableElement'
import { PricingPlanEditorDrawer } from './PricingPlanEditorDrawer'

interface PricingPlanCardProps {
  plan: PricingPlan
  onEdit: (plan: PricingPlan) => void
  activePlan: PricingPlan | undefined
  setActivePlan: SetState<PricingPlan | undefined>
  onTest: (plan: PricingPlan) => void
  onRun: (plan: PricingPlan) => void
  isTestRunning: boolean
  isManualRunning: boolean
  // Whether the user is in the pricing bot beta (featureFlags.pricingBotBeta).
  // Drives the new-UI drawer vs the legacy editor; the underlying plan is the
  // same pricingPlans entry either way.
  isPricingBotBeta: boolean
  // If true at mount, the card immediately opens the new-UI drawer. Used
  // right after creating a new plan via "Create new pricing program" to
  // drop the user straight into the editor.
  defaultPreviewOpen?: boolean
}

export const PricingPlanCard = memo(
  ({
    plan,
    onEdit,
    activePlan,
    setActivePlan,
    onTest,
    onRun,
    isTestRunning,
    isManualRunning,
    isPricingBotBeta,
    defaultPreviewOpen,
  }: PricingPlanCardProps): JSX.Element => {
    const { t } = useTranslation()
    const { user, hasPermission } = useUser()
    const handleChange = useHandleSettingsChange()
    const [previewOpen, setPreviewOpen] = useState<boolean>(defaultPreviewOpen ?? false)
    const [leaveBetaOpen, setLeaveBetaOpen] = useState(false)
    // setUser in the parent triggers the re-render BEFORE setNewlyCreatedPlanId,
    // so the card can mount with defaultPreviewOpen=false and receive true on
    // the next render. The state initializer doesn't react to prop changes
    // post-mount → we react here.
    useEffect(() => {
      if (defaultPreviewOpen) setPreviewOpen(true)
    }, [defaultPreviewOpen])

    const [confirmDeletePlan, ConfirmDeletePlanDialog] = useConfirm(
      t('common.confirmDelete'),
      '',
      async () => await handleChange(() => deleteUserSubEntity(plan._id, 'pricingPlans'))
    )

    const changeSettings = async (
      attribute: keyof PricingPlan['settings'],
      plan: PricingPlan,
      newValue: unknown
    ): Promise<void> => {
      await handleChange(() =>
        editUserSubEntity(
          {
            ...plan,
            settings: {
              ...plan.settings,
              [attribute]:
                attribute === 'maxValueIncreaseEnabled' ? Boolean(newValue) : Number(newValue),
            },
          },
          'pricingPlans'
        )
      )
    }

    return (
      <PowerToolsPaper className="pricing-plan-card">
        <div className="pricing-plan-card__title">
          <h6>
            <EditableElement
              value={plan.name}
              attribute="name"
              type="textfield"
              title
              viewOnly={false}
              onChange={(newValue) =>
                handleChange(() =>
                  editUserSubEntity({ ...plan, name: newValue as string }, 'pricingPlans')
                )
              }
              existingValue={plan.name}
              tooltipText={t('strategy.name')}
              name={`${t('priceUpdates.pricingPlan')} ${t('strategy.name')}`}
              dataTestid={`plan-${plan.name}`}
            />
          </h6>
        </div>
        <div className="pricing-plan-card__body d-flex flex-column flex-lg-row gap-3 align-items-start">
          <div className="pricing-plan-card__settings">
            <div className="pricing-plan-card__setting">
              <div className="pricing-plan-card_icons">
                <i className="fa-light fa-arrow-down"></i>
              </div>
              <Typography variant="body2" className="pricing-plan-card__setting-text">
                {t('priceUpdates.planSettings.maxValueDecrease')}:{' '}
                <EditableElement
                  onChange={(newValue) =>
                    changeSettings('maxValueDecrease', plan, Number(newValue) >= 0 ? newValue : 5)
                  }
                  name={t('priceUpdates.planSettings.maxValueDecrease')}
                  viewOnly={false}
                  type="number"
                  existingValue={plan.settings.maxValueDecrease}
                  tooltipText={t('priceUpdates.planSettings.maxValueDecreaseExplainer')}
                  value={`${plan.settings.maxValueDecrease}%`}
                  showBorder
                  dataTestid="pricing-plan-settings-maxValueDecrease"
                />
              </Typography>
            </div>
            <div className="pricing-plan-card__setting">
              <div className="pricing-plan-card_icons">
                <i className="fa-light fa-arrow-up"></i>
              </div>
              <Typography variant="body2" className="pricing-plan-card__setting-text">
                {t('priceUpdates.planSettings.maxValueIncrease')}:
                <Checkbox
                  size="small"
                  checked={plan.settings.maxValueIncreaseEnabled}
                  onChange={(e) =>
                    changeSettings('maxValueIncreaseEnabled', plan, e.target.checked)
                  }
                />
              </Typography>
              {plan.settings.maxValueIncreaseEnabled && (
                <EditableElement
                  onChange={(newValue) =>
                    changeSettings('maxValueIncrease', plan, Number(newValue) >= 0 ? newValue : 10)
                  }
                  name={t('priceUpdates.planSettings.maxValueIncrease')}
                  viewOnly={false}
                  type="number"
                  existingValue={plan.settings.maxValueIncrease}
                  tooltipText={t('priceUpdates.planSettings.maxValueIncreaseExplainer')}
                  value={`${plan.settings.maxValueIncrease}%`}
                  showBorder
                  dataTestid="pricing-plan-settings-maxValueIncrease"
                />
              )}
            </div>
            <div className="pricing-plan-card__setting">
              <div className="pricing-plan-card_icons">
                <i className="fa-light fa-arrow-trend-up"></i>
              </div>
              <Typography variant="body2" className="pricing-plan-card__setting-text">
                {t('priceUpdates.planSettings.minPriceChange')}:{' '}
                <EditableElement
                  onChange={(newValue) =>
                    changeSettings('minPriceChange', plan, Number(newValue) >= 0.1 ? newValue : 0.1)
                  }
                  name={t('priceUpdates.planSettings.minPriceChange')}
                  viewOnly={false}
                  type="number"
                  existingValue={plan.settings.minPriceChange || 0.1}
                  tooltipText={t('priceUpdates.planSettings.minPriceChangeExplainer')}
                  value={`${plan.settings.minPriceChange || 0.1}%`}
                  showBorder
                  dataTestid="pricing-plan-settings-minPriceChange"
                />
              </Typography>
            </div>
            <div className="pricing-plan-card__setting">
              <div className="pricing-plan-card_icons">
                <i className="fa-light fa-moon"></i>
              </div>
              <Typography variant="body2" className="pricing-plan-card__activate-label">
                {t('priceUpdates.activate')}
              </Typography>
              <Switch
                data-testid="pricing-bot-switch"
                onChange={(e) => {
                  setActivePlan(e.target.checked ? plan : undefined)
                }}
                checked={plan._id === activePlan?._id}
                color="primary"
                disabled={user?.subscription.featuresDisabled.includes('pricingBot')}
              />
            </div>
          </div>
          <div className="pricing-plan-card__actions">
            <ActionButton
              color="secondary"
              onClick={() => onTest(plan)}
              tooltip={t('priceUpdates.explainTest')}
              text={t('priceUpdates.test')}
              data-testid={`test-plan-${plan.name}`}
              disabled={isTestRunning || isManualRunning}
              icon="fa-sharp fa-light fa-vial"
            />
            {hasPermission('use_internal_tools') && (
              <ActionButton
                color="secondary"
                onClick={() => onRun(plan)}
                tooltip={t('priceUpdates.explainManualRun')}
                text={t('priceUpdates.manualRun')}
                data-test-id={`manual-run-plan-${plan.name}`}
                disabled={isTestRunning || isManualRunning}
                icon="fa-light fa-hand-pointer"
              />
            )}
            <ActionButton
              color="secondary"
              onClick={() => (isPricingBotBeta ? setPreviewOpen(true) : onEdit(plan))}
              tooltip={t('priceUpdates.explainEdit')}
              text={t('common.edit')}
              data-test-id={`edit-plan-${plan.name}`}
              disabled={isTestRunning || isManualRunning}
              icon="fa-light fa-pen"
            />
            {!isPricingBotBeta && (
              <ActionButton
                color="secondary"
                onClick={() => setPreviewOpen(true)}
                text={t('priceUpdates.viewBeta')}
                data-test-id={`view-beta-plan-${plan.name}`}
                disabled={isTestRunning || isManualRunning}
                icon="fa-light fa-flask"
              />
            )}
            <ActionButton
              color="error"
              onClick={async () => {
                confirmDeletePlan()
              }}
              tooltip={t('priceUpdates.explainDelete')}
              text={t('common.delete')}
              data-test-id={`delete-plan-${plan.name}`}
              disabled={isTestRunning || isManualRunning}
              icon="fa-light fa-trash"
            />
          </div>
        </div>

        {isPricingBotBeta && (
          <div className="d-flex justify-content-end">
            <Button size="small" color="primary" onClick={() => setLeaveBetaOpen(true)}>
              {t('priceUpdates.leaveBetaLink')}
            </Button>
          </div>
        )}

        <PricingPlanEditorDrawer
          plan={plan}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          startInEditNameMode={defaultPreviewOpen}
          preview={!isPricingBotBeta}
        />

        <ConfirmDeletePlanDialog />
        {/* Leaving restores the backup, which only the team can do: the user asks via a support ticket */}
        <BugReportDialog
          open={leaveBetaOpen}
          onClose={() => setLeaveBetaOpen(false)}
          fixedSubject={t('priceUpdates.leaveBetaSubject')}
          defaultDescription={t('priceUpdates.leaveBetaMessage')}
          hideAttachments
        />
      </PowerToolsPaper>
    )
  }
)
